import type { FormProduct } from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteDraftDialog } from "./delete-draft-dialog";
import { SsProductListModal } from "./ss-product-list-modal";
import { setSsSkuDraftsModalTestId } from "./ss-sku-drafts-modal.utils";

interface SsSkuDraftsModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onResume: (draft: FormProduct) => void;
  readonly onDelete: (draft: FormProduct) => Promise<boolean> | Promise<void> | void;
  readonly drafts: FormProduct[];
  readonly testId?: string;
}

/** Lists saved in-progress SKU drafts so an SS roaster can resume or delete
 *  one. Thin wrapper over SsProductListModal; owns the delete-confirmation
 *  state itself, following the DisconnectProductDialog ownership pattern. */
export function SsSkuDraftsModal({ open, onOpenChange, onResume, onDelete, drafts, testId }: SsSkuDraftsModalProps) {
  const { t } = useTranslation();
  const [pendingDeleteDraft, setPendingDeleteDraft] = useState<FormProduct | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!pendingDeleteDraft) return;
    setIsDeleting(true);
    try {
      await onDelete(pendingDeleteDraft);
    } finally {
      setIsDeleting(false);
      setPendingDeleteDraft(undefined);
    }
  };

  const handleDeleteDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isDeleting) {
      setPendingDeleteDraft(undefined);
    }
  };

  return (
    <>
      <SsProductListModal
        open={open}
        onOpenChange={onOpenChange}
        onAction={onResume}
        onSecondaryAction={setPendingDeleteDraft}
        products={drafts}
        testId={testId}
        setTestId={setSsSkuDraftsModalTestId}
        rowTestIdPrefix="draft"
        actionTestIdPrefix="resume"
        secondaryActionTestIdPrefix="delete"
        labels={{
          title: t("ssRoaster.drafts.title"),
          description: t("ssRoaster.drafts.description"),
          searchPlaceholder: t("ssRoaster.drafts.searchPlaceholder"),
          noResults: t("ssRoaster.drafts.noResults"),
          untitledProduct: t("ssRoaster.drafts.untitledProduct"),
          actionLabel: t("ssRoaster.drafts.resume"),
          secondaryActionLabel: t("ssRoaster.drafts.delete"),
        }}
      />
      <DeleteDraftDialog
        open={!!pendingDeleteDraft}
        onOpenChange={handleDeleteDialogOpenChange}
        draftTitle={pendingDeleteDraft?.title ?? t("ssRoaster.drafts.untitledProduct")}
        onConfirm={handleConfirmDelete}
        isPending={isDeleting}
      />
    </>
  );
}
